// Getting from example integrate next.js with apollo
// https://github.com/kellenmace/apollo-client-cache-rehydration-in-next-js/blob/main/lib/apolloClient.ts
// https://github.com/vercel/next.js/blob/canary/examples/with-apollo/lib/apolloClient.js
import {
  ApolloClient,
  HttpLink,
  from,
  Operation,
  NextLink,
  ApolloLink,
} from '@apollo/client';
import { GetServerSidePropsContext } from 'next';
import type { NormalizedCacheObject } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RestLink } from 'apollo-link-rest';
import { useMemo } from 'react';
import merge from 'deepmerge';
import isEqual from 'lodash.isequal';
import { omitDeep } from '@patomation/omit-deep';

import { IS_SERVER, IS_PRODUCTION } from 'utils/envs';

import { createCache } from './cache';

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

//линка нужна для очистки полей __typename из мутаций
const typenameLink = new ApolloLink(
  (operation: Operation, forward: NextLink) => {
    const definition = operation?.query?.definitions.filter(
      (def: any) => def.kind === 'OperationDefinition'
    )?.[0];
    if (
      definition?.kind == 'OperationDefinition' &&
      definition?.operation === 'mutation'
    ) {
      operation.variables = omitDeep(operation.variables, '__typename');
      return forward(operation);
    }
    return forward(operation);
  }
);

const restLink = new RestLink({
  uri: process.env.NEXT_PUBLIC_API_HOST,
  typePatcher: { MetaData: (data: any) => data },
});

export function createApolloClient(cookie: string) {
  const httpLink = new HttpLink({
    uri: process.env.NEXT_PUBLIC_API_URL,
    credentials: 'include',
    fetch: (request, options) => {
      const headers: HeadersInit = {
        ...(options?.headers || {}),
        cookie,
      };

      return fetch(request, { ...options, headers });
    },
  });

  const errorLink = onError(({ networkError, response }) => {});

  return new ApolloClient({
    link: from([errorLink, typenameLink, restLink, httpLink]),
    cache: createCache(),
    ssrMode: IS_SERVER,
    connectToDevTools: !IS_PRODUCTION,
  });
}

export interface InitializeApollo {
  initialState?: NormalizedCacheObject;
  ctx?: GetServerSidePropsContext;
}

export default function initializeApollo(args?: InitializeApollo) {
  const apolloInstance: ApolloClient<NormalizedCacheObject> =
    apolloClient ?? createApolloClient(args?.ctx?.req.headers.cookie || '');

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (args?.initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = apolloInstance.extract();

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(args.initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s))
        ),
      ],
    });

    // Restore the cache with the merged data
    apolloInstance.cache.restore(data);
  }
  // For SSG and SSR always create a new Apollo Client
  if (IS_SERVER) return apolloInstance;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = apolloInstance;

  return apolloInstance;
}

export function addApolloState(
  client: ApolloClient<NormalizedCacheObject>,
  pageProps: any
) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

export function useApollo(pageProps: any) {
  const initialState = pageProps[APOLLO_STATE_PROP_NAME];
  return useMemo(() => initializeApollo({ initialState }), [initialState]);
}
